import { Controller } from "@hotwired/stimulus"
import { escapeHtml } from "../escape_html"

// Searchable food picker.
//
// Fetches matches from the server as you type rather than filtering a
// preloaded list: the library is ~8,000 foods after USDA ingestion, which is
// far too many to render as <option> elements on every ingredient row.
//
// The <select> stays in the DOM holding only the CURRENT value, so the form
// still submits a real food_id and this degrades to a plain (if unsearchable)
// control if the JS fails.
export default class extends Controller {
  static targets = ["select", "input", "list"]
  static values = { url: String, newFoodUrl: String, choseUrl: String }

  connect() {
    this.activeIndex = -1
    this.results = []
    this.debounce = null
    this.selectTarget.classList.add("sr-only")
    this.selectTarget.setAttribute("tabindex", "-1")
    this.syncInputFromSelect()

    // A click anywhere else closes the list.
    //
    // Blur alone was not enough: clicking a heading, a card or empty page
    // moves focus nowhere, so no blur fires and the list stayed open behind
    // whatever was clicked — and with a row per ingredient, several could be
    // open at once.
    //
    // mousedown with capture, so it beats the list's own mousedown for a
    // click landing outside; a click inside is let through by the containment
    // check.
    this.onOutsideClick = (event) => {
      if (this.listTarget.hidden) return
      if (this.element.contains(event.target)) return

      this.close()
    }
    document.addEventListener("mousedown", this.onOutsideClick, true)
  }

  disconnect() {
    clearTimeout(this.debounce)
    document.removeEventListener("mousedown", this.onOutsideClick, true)
  }

  syncInputFromSelect() {
    const selected = this.selectTarget.selectedOptions[0]
    this.inputTarget.value = selected && selected.value !== "" ? selected.text : ""
  }

  open() {
    this.search()
  }

  // A blur landing on the list is a click on an option, not a dismissal.
  close(event) {
    if (event?.relatedTarget && this.listTarget.contains(event.relatedTarget)) return

    this.listTarget.hidden = true
    this.activeIndex = -1
    this.syncInputFromSelect()
  }

  // Typing fires per keystroke; the request should not.
  filter() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.search(), 180)
  }

  async search() {
    const query = this.inputTarget.value.trim()

    this.listTarget.hidden = false
    if (this.results.length === 0) {
      this.listTarget.innerHTML = `<div class="px-3 py-2.5 text-sm text-ink-faint">Searching…</div>`
    }

    // ONE ID FOR ONE TYPING RUN. Past the 180ms debounce, "chicken breast"
    // fires several searches — "chick", "chicke", "chicken" — and only the
    // last is the query the person meant. Sharing an id lets the server
    // collapse those prefixes back together later, and pairs the pick with
    // the results it was picked from. Reset on commit, not per keystroke.
    this.searchId ||= `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
    this.searchedFor = query

    try {
      const response = await fetch(
        `${this.urlValue}?q=${encodeURIComponent(query)}&search_id=${this.searchId}`,
        { headers: { Accept: "application/json" } })
      if (!response.ok) return

      this.results = await response.json()
      this.render()
      this.activeIndex = this.results.length > 0 ? 0 : -1
      this.highlight()
    } catch {
      // A dropped search is not worth surfacing — the next keystroke retries.
    }
  }

  render() {
    if (this.results.length === 0) {
      // Offer to create it rather than sending someone off to another page
      // and losing the recipe they were part way through.
      const query = this.inputTarget.value.trim()
      this.listTarget.innerHTML = `
        <div class="px-3 py-2.5 text-sm text-ink-faint">Nothing matches “${this.escape(query)}”.</div>
        <button type="button" data-action="mousedown->combobox#createNew"
                class="combobox-option flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-ink">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          Add “${this.escape(query)}” as a new food
        </button>`
      return
    }

    this.listTarget.innerHTML = this.results.map((food, index) => `
      <button type="button" role="option" data-index="${index}"
              data-action="mousedown->combobox#choose mouseenter->combobox#hover"
              class="combobox-option flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm text-ink">
        <span class="truncate">${food.brand
          ? `<span class="font-semibold text-brand-deep">${this.escape(food.brand)}</span><span class="text-ink-faint"> · </span>`
          : ""}${food.matched_on
          ? `${this.escape(this.titleise(food.matched_on))} <span class="text-ink-faint">(${this.escape(food.name)})</span>`
          : this.escape(food.name)}</span>
        ${food.estimated ? `<span class="shrink-0 text-[0.6875rem] uppercase tracking-wide text-ink-faint" title="Typical values \u2014 not laboratory measured">Est.</span>` : ""}
      </button>`).join("")
  }

  // THE BRAND LEADS THE NAME, and is not a chip.
  //
  // Mirrors shared/_food_name, which is the one treatment everywhere a food
  // is shown — brand in brand-deep, a middot, then the name. This list drew
  // it as a chip on the right instead, which is the treatment the rest of
  // the app dropped: a chip is for a caveat, and a brand is library data on
  // every branded row. Cody, 11 Sep.
  //
  // Duplicated from the partial rather than fetched because this list is
  // built in the browser from a JSON search response. The rule lives in
  // shared/_food_name; a spec fails if the two disagree.

  // A search term that matched an alias is shown first, so typing "rocket"
  // does not come back looking like it found the wrong food. The food's real
  // name follows in brackets — it is what gets stored, so it must stay
  // visible rather than be replaced.
  titleise(term) {
    return term.charAt(0).toUpperCase() + term.slice(1)
  }

  hover(event) {
    this.activeIndex = Number(event.currentTarget.dataset.index)
    this.highlight()
  }

  highlight() {
    this.listTarget.querySelectorAll(".combobox-option").forEach((element, index) => {
      element.classList.toggle("is-active", index === this.activeIndex)
    })
  }

  // mousedown, not click: the input's blur would close the list first.
  choose(event) {
    event.preventDefault()
    this.commit(Number(event.currentTarget.dataset.index))
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.listTarget.hidden) return this.open()
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1)
        this.highlight()
        this.scrollActiveIntoView()
        break
      case "ArrowUp":
        event.preventDefault()
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.highlight()
        this.scrollActiveIntoView()
        break
      case "Enter":
        if (this.listTarget.hidden || this.activeIndex < 0) return
        event.preventDefault()
        this.commit(this.activeIndex)
        break
      case "Escape":
        this.close()
        break
    }
  }

  commit(index) {
    const food = this.results[index]
    if (!food) return

    this.reportChoice(food, index + 1)

    // The select only ever holds the chosen food, so the form posts a real id
    // without carrying the whole library.
    // THE SERVER'S OWN LABEL, not a second attempt at it. An <option>
    // cannot hold markup, so a branded food reads as one string here — and
    // which string is Food#picker_label's business, not this controller's.
    // Rebuilding it as "Name (Brand)" meant the text changed under you on
    // reload. Falls back only if an older payload lacks the field.
    const label = food.picker_label ||
      (food.brand ? `${food.brand} ${food.name}` : food.name)
    this.selectTarget.innerHTML = `<option value="${food.id}" selected>${this.escape(label)}</option>`
    this.selectTarget.value = String(food.id)
    this.selectTarget.dataset.units = JSON.stringify(food.units)
    // Raw, dry or cooked, for the row's weight notice — see
    // ingredients#foodChanged. Empty where the food's weight does not
    // change with cooking, and the notice stays hidden.
    this.selectTarget.dataset.weightState = food.weight_state || ""

    // What the method would have to say to be talking about this food.
    //
    // Written onto the ROW, where the server writes it for foods that were
    // already chosen when the page rendered. A row added mid-edit is cloned
    // from a template with no food, so this is the only moment those words
    // can arrive — without it, an ingredient you just added is never checked
    // against the method. See orphan_check_controller.
    this.markRow(food)

    // The units dropdown listens for this on the real select.
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.inputTarget.value = label
    this.listTarget.hidden = true

    // The run is over. The next keystroke starts a new one, so a second
    // pick from the same row is not attributed to the first search.
    this.searchId = null
  }

  // WHICH ONE THEY TOOK, AND FROM WHERE IN THE LIST. The server recorded
  // what it showed; only this knows what was chosen, and position is what
  // makes the pair readable — rank 1 is chosen more whatever it is, so a
  // raw count would just re-confirm the order we already have.
  //
  // DELIBERATELY NOT AWAITED. Selecting a food must not wait on analytics,
  // and there is nothing useful to do if it fails: keepalive lets it finish
  // if the page navigates away, and the catch keeps a dropped beacon from
  // reaching the console as an unhandled rejection.
  reportChoice(food, position) {
    if (!this.hasChoseUrlValue || !this.searchId || !this.searchedFor) return

    const body = new FormData()
    body.append("q", this.searchedFor)
    body.append("food_id", food.id)
    body.append("position", position)
    body.append("search_id", this.searchId)

    const token = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.choseUrlValue, {
      method: "POST",
      body,
      keepalive: true,
      headers: token ? { "X-CSRF-Token": token } : {}
    }).catch(() => {})
  }

  markRow(food) {
    const row = this.element.closest("[data-ingredients-target='row']")
    if (!row || !food.mention_terms?.length) return

    row.dataset.orphanCheckTarget = "row"
    row.dataset.mentionName = food.name
    row.dataset.mentionTerms = JSON.stringify(food.mention_terms)
    row.dataset.mentionOwnTerms = JSON.stringify(food.own_mention_terms || [])

    // Stimulus registers the new target when the attribute lands, but nothing
    // asks the question again — so the row would sit there unchecked until
    // the next keystroke in the method.
    row.dispatchEvent(new CustomEvent("ingredients:changed", { bubbles: true }))
  }

  // Opens the new-food modal so the half-finished recipe stays put.
  //
  // The modal is SERVER-RENDERED into the shared frame, like every other
  // modal in the app, which means the request cannot carry a reference to
  // this controller. So the combobox stamps a token on its own element and
  // sends that; new_food looks the element back up when the food is
  // ready and calls adopt() on whatever controller is attached.
  //
  // A token rather than the row's position: a modal scroll-locks the page
  // underneath so rows cannot be dragged mid-create, but a handle that
  // survives a reorder costs nothing and removes the question.
  createNew(event) {
    event.preventDefault()

    this.element.dataset.comboboxToken ||= `cb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    const params = new URLSearchParams({
      name: this.inputTarget.value.trim(),
      token: this.element.dataset.comboboxToken
    })

    // Into the shared modal frame, like every other modal in the app. Turbo
    // needs a real navigation to target a frame, so this is a link click
    // rather than a fetch — the frame swap, the history entry and the
    // focus handling all come for free that way.
    const link = document.createElement("a")
    link.href = `${this.newFoodUrlValue}?${params}`
    link.dataset.turboFrame = "modal"
    link.hidden = true
    document.body.append(link)
    link.click()
    link.remove()
  }

  // Called back by the dialog once a food has been created.
  adopt(food) {
    this.results = [ food ]
    this.commit(0)
  }

  scrollActiveIntoView() {
    this.listTarget.querySelectorAll(".combobox-option")[this.activeIndex]
      ?.scrollIntoView({ block: "nearest" })
  }

  // One shared implementation — see escape_html.js.
  escape(text) { return escapeHtml(text) }
}
